/**
 * AchievementRepository - Database operations for achievements collection
 */

import { BaseRepository, where, limit, orderBy, Timestamp } from '../../discipline/repositories/BaseRepository';
import { arrayUnion, collection, query as firestoreQuery, getDocs } from 'firebase/firestore';
import { typeNormalizer } from '../../discipline/utils/TypeNormalizer';
import { db } from '../../../firebase';

export class AchievementRepository extends BaseRepository {
  constructor() {
    // CRITICAL: Enable hierarchical paths - achievements is now stored at schools/{schoolId}/achievements/{docId}
    super('achievements', { isHierarchical: true });
  }

  /**
   * Query hierarchical path only (migration completed)
   */
  async queryBothPaths(constraints = [], schoolId = null) {
    console.log('[AchievementRepo] 🔍 queryBothPaths called', { schoolId, constraintsCount: constraints.length });

    if (!schoolId) {
      console.warn('[AchievementRepo] ⚠️ schoolId required for hierarchical query');
      return [];
    }

    let allDocs = [];

    try {
      console.log('[AchievementRepo] 📂 Querying hierarchical path: schools/' + schoolId + '/achievements');
      const hierarchicalRef = collection(db, 'schools', schoolId, 'achievements');
      const hierarchicalQuery = firestoreQuery(hierarchicalRef, ...constraints);
      const hierarchicalSnapshot = await getDocs(hierarchicalQuery);
      console.log('[AchievementRepo] ✅ Hierarchical query returned', hierarchicalSnapshot.size, 'docs');
      allDocs.push(...hierarchicalSnapshot.docs);
    } catch (error) {
      console.error('[AchievementRepo] ❌ Hierarchical query failed:', error.message);
      throw error;
    }

    return allDocs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get achievements for a specific student
   * @param {string} studentId - Student ID
   * @param {Object} options - Query options (must include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of achievements
   */
  async getByStudentId(studentId, options = {}) {
    const constraints = [
      where('studentId', '==', studentId)
    ];

    if (options.status) {
      constraints.push(where('status', '==', options.status));
    }

    if (options.type) {
      // Try to get typeId first for newer records
      const typeId = await typeNormalizer.normalizeToId(options.type);
      if (typeId) {
        // Query by typeId (preferred for new records)
        constraints.push(where('typeId', '==', typeId));
      } else {
        // Fallback to string type for legacy records
        const normalizedType = await typeNormalizer.normalize(options.type, options.schoolId);
        constraints.push(where('type', '==', normalizedType));
      }
    }

    if (options.revoked !== undefined) {
      constraints.push(where('revoked', '==', options.revoked));
    }

    if (options.startDate) {
      constraints.push(where('issuedAt', '>=', Timestamp.fromDate(options.startDate)));
    }

    if (options.endDate) {
      constraints.push(where('issuedAt', '<=', Timestamp.fromDate(options.endDate)));
    }

    constraints.push(orderBy('issuedAt', 'desc'));

    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    // CRITICAL: Use queryBothPaths to query both hierarchical and flat collections during migration
    return this.queryBothPaths(constraints, options.schoolId);
  }

  /**
   * Get pending achievements requiring action
   * @param {Object} filters - Filter options (must include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of pending achievements
   */
  async getPendingAchievements(filters = {}) {
    const constraints = [
      where('status', '==', 'pending'),
      where('revoked', '==', false)
    ];

    if (filters.type) {
      // Try to get typeId first for newer records
      const typeId = await typeNormalizer.normalizeToId(filters.type);
      if (typeId) {
        constraints.push(where('typeId', '==', typeId));
      } else {
        const normalizedType = await typeNormalizer.normalize(filters.type, filters.schoolId);
        constraints.push(where('type', '==', normalizedType));
      }
    }

    if (filters.classId) {
      constraints.push(where('classId', '==', filters.classId));
    }

    constraints.push(orderBy('issuedAt', 'desc'));

    // CRITICAL: Use queryBothPaths to query both hierarchical and flat collections during migration
    return this.queryBothPaths(constraints, filters.schoolId);
  }

  /**
   * Get badges for a specific date
   * @param {Date} date - Date to query
   * @param {Object} filters - Additional filters (must include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of badges
   */
  async getBadgesByDate(date, filters = {}) {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    // Get typeIds for badge types
    const badgeTypeIds = [];
    const badgeId = await typeNormalizer.normalizeToId('badge');
    const saturdayId = await typeNormalizer.normalizeToId('saturday-badge');

    if (badgeId) badgeTypeIds.push(badgeId);
    if (saturdayId) badgeTypeIds.push(saturdayId);

    const constraints = [];

    // Use typeId if available, otherwise fallback to type strings
    if (badgeTypeIds.length > 0) {
      constraints.push(where('typeId', 'in', badgeTypeIds));
    } else {
      constraints.push(where('type', 'in', ['badge', 'saturday-badge']));
    }

    constraints.push(
      where('scheduledDate', '>=', Timestamp.fromDate(startOfDay)),
      where('scheduledDate', '<=', Timestamp.fromDate(endOfDay)),
      where('revoked', '==', false)
    );

    if (filters.claimed !== undefined) {
      constraints.push(where('claimed', '==', filters.claimed));
    }

    constraints.push(orderBy('scheduledDate', 'asc'));

    // CRITICAL: Use queryBothPaths to query both hierarchical and flat collections during migration
    return this.queryBothPaths(constraints, filters.schoolId);
  }

  /**
   * Mark badge as claimed
   * @param {string} badgeId - Badge ID
   * @param {Object} claimedDetails - Details about serving
   * @returns {Promise<Object>} Updated badge
   */
  async markBadgeClaimed(badgeId, claimedDetails = {}) {
    return this.update(badgeId, {
      claimed: true,
      claimedAt: Timestamp.now(),
      claimedBy: claimedDetails.claimedBy || 'system',
      status: 'archived',
      notes: claimedDetails.notes || ''
    });
  }

  /**
   * Undo a achievement
   * @param {string} achievementId - Achievement ID
   * @param {Object} undoDetails - Details about the undo
   * @returns {Promise<Object>} Updated achievement
   */
  async undoAchievement(achievementId, undoDetails = {}) {
    return this.update(achievementId, {
      revoked: true,
      revokedAt: Timestamp.now(),
      revokedBy: undoDetails.revokedBy || 'system',
      revokedReason: undoDetails.reason || 'Manual undo',
      status: 'revoked'
    });
  }

  /**
   * Get recognitions for threshold checking
   * @param {string} studentId - Student ID
   * @param {Date} startDate - Start date for period
   * @returns {Promise<Array>} Array of recognitions
   */
  async getRecognitionsForThreshold(studentId, startDate) {
    const constraints = [
      where('studentId', '==', studentId)
    ];

    // Try to get typeId for recognition
    const recognitionTypeId = await typeNormalizer.normalizeToId('recognition');
    if (recognitionTypeId) {
      constraints.push(where('typeId', '==', recognitionTypeId));
    } else {
      constraints.push(where('type', '==', 'recognition'));
    }

    constraints.push(
      where('revoked', '==', false),
      where('issuedAt', '>=', Timestamp.fromDate(startDate))
    );

    constraints.push(orderBy('issuedAt', 'desc'));

    return this.queryDocuments(constraints);
  }

  /**
   * Get total recognition points for a student
   * @param {string} studentId - Student ID
   * @param {Date} startDate - Start date for calculation
   * @returns {Promise<number>} Total recognition points
   */
  async getTotalRecognitionPoints(studentId, startDate) {
    const recognitions = await this.getRecognitionsForThreshold(studentId, startDate);

    return recognitions.reduce((total, recognition) => {
      return total + (recognition.points || recognition.achievementCount || 1);
    }, 0);
  }

  /**
   * Update achievement status
   * @param {string} achievementId - Achievement ID
   * @param {string} newStatus - New status
   * @param {Object} additionalUpdates - Additional fields to update
   * @returns {Promise<Object>} Updated achievement
   */
  async updateStatus(achievementId, newStatus, additionalUpdates = {}) {
    return this.update(achievementId, {
      status: newStatus,
      ...additionalUpdates
    });
  }

  /**
   * Add action to achievement
   * @param {string} achievementId - Achievement ID
   * @param {Object} actionDetails - Details of the action
   * @returns {Promise<Object>} Updated achievement
   */
  async addAction(achievementId, actionDetails) {
    const updates = {
      actioned: true,
      actionedAt: Timestamp.now(),
      actionedBy: arrayUnion(actionDetails.actionedBy || 'system')
    };

    if (actionDetails.notes) {
      updates.notes = actionDetails.notes;
    }

    return this.update(achievementId, updates);
  }

  /**
   * Get recent achievements for statistics
   * @param {number} days - Number of days to look back
   * @param {Object} filters - Optional filters (must include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of recent achievements
   */
  async getRecentAchievements(days = 7, filters = {}) {
    console.log('[AchievementRepo] 🔍 getRecentAchievements called', { days, filters, hasSchoolId: !!filters.schoolId });
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const constraints = [
      where('issuedAt', '>=', Timestamp.fromDate(startDate)),
      where('revoked', '==', false)
    ];

    if (filters.type) {
      // Try to get typeId first for newer records
      const typeId = await typeNormalizer.normalizeToId(filters.type);
      if (typeId) {
        constraints.push(where('typeId', '==', typeId));
      } else {
        const normalizedType = await typeNormalizer.normalize(filters.type, filters.schoolId);
        constraints.push(where('type', '==', normalizedType));
      }
    }

    if (filters.classId) {
      constraints.push(where('classId', '==', filters.classId));
    }

    if (filters.studentId) {
      constraints.push(where('studentId', '==', filters.studentId));
    }

    constraints.push(orderBy('issuedAt', 'desc'));

    // CRITICAL: Use queryBothPaths to query both hierarchical and flat collections during migration
    console.log('[AchievementRepo] 📞 Calling queryBothPaths with schoolId:', filters.schoolId);
    const results = await this.queryBothPaths(constraints, filters.schoolId);
    console.log('[AchievementRepo] ✅ queryBothPaths returned', results.length, 'achievements');
    return results;
  }

  /**
   * Check for existing streak achievement
   * @param {string} studentId - Student ID
   * @param {string} thresholdId - Threshold ID
   * @param {Date} startDate - Period start date
   * @returns {Promise<boolean>} True if exists
   */
  async checkExistingStreakAchievement(studentId, thresholdId, startDate) {
    const constraints = [
      where('studentId', '==', studentId),
      where('streakThreshold', '==', thresholdId),
      where('revoked', '==', false),
      where('createdAt', '>=', Timestamp.fromDate(startDate))
    ];

    const results = await this.queryDocuments(constraints);
    return results.length > 0;
  }

  /**
   * Get achievements by trigger rule
   * @param {string} ruleId - Rule ID that triggered achievements
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of achievements
   */
  async getByTriggerRule(ruleId, options = {}) {
    const constraints = [
      where('triggeredByRule', '==', ruleId)
    ];

    if (options.studentId) {
      constraints.push(where('studentId', '==', options.studentId));
    }

    if (options.revoked !== undefined) {
      constraints.push(where('revoked', '==', options.revoked));
    }

    constraints.push(orderBy('issuedAt', 'desc'));

    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    return this.queryDocuments(constraints);
  }

  /**
   * Subscribe to achievement changes for a student
   * @param {string} studentId - Student ID
   * @param {Function} callback - Callback for changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToStudentAchievements(studentId, callback) {
    const constraints = [
      where('studentId', '==', studentId),
      where('revoked', '==', false),
      orderBy('issuedAt', 'desc')
    ];

    return this.subscribe(callback, constraints);
  }
}